import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Bot, Loader2 } from 'lucide-react';
import { automationService, type Strategy } from '@/services/strategyService';

/**
 * Live automation panel — arm/disarm a strategy for REAL-MONEY live trading.
 * Arming sets the rule's TradingEnvironment=Live and starts the live run; the
 * per-user live kill switch + master switch still gate every order. Two-click
 * confirm because this commits real money.
 *
 * Equity-only today (forex automation has no engine yet). Strategy CRUD still
 * lives on the Automation page; this is the live on/off control.
 */
export const LiveAutomationPanel: React.FC<{ assetClass: 'equity' | 'forex' }> = ({ assetClass }) => {
  const wantClass = assetClass === 'forex' ? 'Forex' : 'Equity';
  const [rules, setRules] = useState<Strategy[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      // Page through ALL rules — getRules() is a 100-cap shim that would hide a
      // user's strategies beyond the first page from the live arm/disarm list.
      const matched: Strategy[] = [];
      const pageSize = 100;
      for (let page = 1; ; page++) {
        const res = await automationService.getRulesPage({ page, pageSize });
        matched.push(...res.items.filter((r) => (r.assetClass || 'Equity') === wantClass));
        if (res.items.length < pageSize || matched.length > 5000) break;
      }
      setRules(matched);
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Failed to load strategies');
    } finally {
      setLoaded(true);
    }
  }, [wantClass]);

  useEffect(() => { void refresh(); }, [refresh]);

  return (
    <div className="space-y-3 max-w-2xl">
      <div className="flex items-start gap-2 rounded-md border border-red-500/40 bg-red-500/5 p-3 text-xs">
        <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
        <span className="text-muted-foreground">
          Arming a strategy here makes it trade <span className="font-semibold text-red-500">real money</span> on
          your live account. Your live kill switch and the master switch must also be on. Disarm any time —
          it stops and reverts to paper.
        </span>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Bot className="h-4 w-4 text-primary" /> Live strategies ({wantClass})
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {error && <div className="text-xs text-red-500 mb-2 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> {error}</div>}
          {!loaded ? (
            <div className="text-xs text-muted-foreground flex items-center gap-1"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…</div>
          ) : rules.length === 0 ? (
            <div className="text-xs text-muted-foreground">
              No {wantClass.toLowerCase()} strategies yet. Create one on the Automation page, then arm it here for live.
            </div>
          ) : (
            <div className="rounded-md border border-border/60 divide-y divide-border/60">
              {rules.map((r) => <LiveRow key={r.id} rule={r} onChanged={refresh} />)}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const LiveRow: React.FC<{ rule: Strategy; onChanged: () => Promise<void> }> = ({ rule, onChanged }) => {
  const armed = (rule.tradingEnvironment || 'Paper') === 'Live';
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Decay the armed-confirm after a few seconds so a "Confirm — go LIVE" button
  // left waiting can't be clicked much later without re-establishing intent.
  useEffect(() => {
    if (!confirm) return;
    const t = setTimeout(() => setConfirm(false), 6000);
    return () => clearTimeout(t);
  }, [confirm]);

  const act = async () => {
    if (!armed && !confirm) { setConfirm(true); return; }   // confirm before arming real money
    setBusy(true); setErr(null);
    try {
      await automationService.setLiveAutomation(rule.id, !armed);
      setConfirm(false);
      await onChanged();
    } catch (e: any) {
      setErr(e?.message || 'Action failed');
      setConfirm(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center justify-between gap-3 p-2.5">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium truncate">{rule.name}</span>
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${
            armed ? 'bg-red-500/15 text-red-500' : 'bg-muted text-muted-foreground'
          }`}>
            <span className={`h-1.5 w-1.5 rounded-full ${armed ? 'bg-red-500' : 'bg-muted-foreground/50'}`} />
            {armed ? 'LIVE' : 'paper'}
          </span>
        </div>
        {err && <div className="text-[11px] text-red-500 mt-0.5 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> {err}</div>}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {confirm && !busy && (
          <Button size="sm" variant="ghost" onClick={() => setConfirm(false)}>Cancel</Button>
        )}
        <Button size="sm" disabled={busy}
          variant={armed ? 'outline' : (confirm ? 'destructive' : 'default')}
          onClick={() => void act()}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
          {armed ? 'Disarm' : confirm ? 'Confirm — go LIVE' : 'Arm live'}
        </Button>
      </div>
    </div>
  );
};

export default LiveAutomationPanel;
